import gql from 'graphql-tag'
import { AggregationConfigs } from '../aggregations'
import { ApplicationImportance } from '../application-importance'
import { Role } from '@contrast-security-inc/base-types'
import { AgentLanguage } from '../agent-language'
import { BugTracker } from '../bug-tracker'
import { SinkGroupSeverityName } from '../vulnerability-severity/sink-group-severity'
import { SinkGroupStatusName } from '../vulnerability-status/sink-group-status'
import { VulnerabilityStatus } from '../../graphql/schema/shared'

export type DateTime = string

export type VulnerabilityDateField = 'FIRST' | 'LAST'
export interface Vulnerability {
  readonly id: string
  readonly title: string
  readonly ruleName: string
  readonly visible: boolean
  readonly severity: VulnerabilitySeverity
  readonly defaultSeverity: VulnerabilitySeverity
  readonly tags: string[]
  readonly application: VulnerabilityApplication
  readonly lastDetected: DateTime
  readonly firstDetected: DateTime
  readonly status: string
  readonly substatus?: string
  readonly reportedToBugTracker: boolean
  readonly reportedToBugTrackerTime?: DateTime
  readonly bugTrackers: BugTracker[]
  readonly violations: PolicyViolation[]
  readonly pendingStatus?: PendingStatus
  readonly sessions?: Session[]
  readonly protectStatus?: string
}
export interface PendingStatus {
  readonly status: VulnerabilityStatus
  readonly created: string
}
export interface Session {
  readonly sessionID: string
  readonly metadata: SessionMetadata[]
}

export interface SessionMetadata {
  readonly agentLabel: string
  readonly displayLabel: string
  readonly value: string
}

export interface PolicyViolation {
  readonly name: string
  readonly id: string
}

export interface VulnerabilityApplication {
  readonly id: string
  readonly name: string
  readonly url: string
  readonly importance: ApplicationImportance
  readonly licensed: boolean
  readonly language: AgentLanguage
  readonly role: Role
  readonly parentApplicationId?: string
  readonly isParentApp?: boolean
}

export interface SinkGroup {
  readonly application?: VulnerabilityApplication
  readonly count: number
  readonly title: string
  readonly firstDetected: DateTime
  readonly lastDetected: DateTime
  readonly ruleName: string
  readonly severity: SinkGroupSeverityName
  readonly sessions: Session[]
  readonly sink: string
  readonly sinkHash: string
  readonly status: SinkGroupStatusName
  readonly substatus?: string
  readonly protectStatus?: string
}

export interface VulnerabilityOrSinkGroup {
  readonly id: string
  readonly sinkGroup?: SinkGroup
  readonly vulnerability?: Vulnerability
}

interface ProtectVulnerability {
  readonly protectStatus?: string
}

export interface VulnerabilityProtectStatus {
  readonly vulnerability?: ProtectVulnerability
}

export enum VulnerabilityGridFilters {
  QUICK_FILTER = 'quickFilter',
  SEVERITIES = 'severities'
}

export type VulnerabilitySeverity =
  | 'NOTE'
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH'
  | 'CRITICAL'

export const vulnerabilityOrSinkGroupFragment = gql`
  fragment VulnerabilityOrSinkGroup on VulnerabilityOrSinkGroup {
    id
    vulnerability {
      id
      title
      ruleName
      visible
      severity
      defaultSeverity
      tags
      application {
        id
        name
        url
        importance
        licensed
        language
        role
        parentApplicationId
        isParentApp
      }
      lastDetected
      firstDetected
      status
      substatus
      reportedToBugTracker
      reportedToBugTrackerTime
      bugTrackers {
        name
        type
        creation
        ticketKey
        ticketTitle
        ticketUrl
      }
      violations {
        id
        name
      }
      pendingStatus {
        status
        created
      }
      sessions {
        sessionID
        metadata {
          agentLabel
          displayLabel
          value
        }
      }
      protectStatus
    }
    sinkGroup {
      application {
        id
        name
        url
        importance
        licensed
        language
        role
        parentApplicationId
        isParentApp
      }
      count
      lastDetected
      firstDetected
      ruleName
      status
      substatus
      severity
      title
      sessions {
        sessionID
        metadata {
          agentLabel
          displayLabel
          value
        }
      }
      sink
      sinkHash
      protectStatus
    }
  }
`

export type AllAggregations =
  | 'servers'
  | 'tags'
  | 'severities'
  | 'statuses'
  | 'bugtrackerStatuses'
  | 'substatus'
  | 'urls'
  | 'securityStandards'
  | 'types'
  | 'buildNumbers'
  | 'environments'
  | 'modules'
  | 'applications'
  | 'routes'
  | 'technologies'
  | 'applicationTags'
  | 'applicationImportances'
  | 'languages'
  | 'sinks'
  | 'protectStatuses'

const labeledAggregationConfigFns = {
  bucketToID: ({ key: { id } }) => id,
  bucketToString: ({ key: { label } }) => label
}

export const allAggregationConfigs: AggregationConfigs<AllAggregations> = {
  tags: {
    groupName: { id: 'title_tags_vulnerability' }
  },
  statuses: {
    groupName: { id: 'title_status' },
    ...labeledAggregationConfigFns
  },
  substatus: {
    groupName: { id: 'substatus' },
    ...labeledAggregationConfigFns
  },
  securityStandards: {
    groupName: { id: 'title_compliancePolicies' },
    ...labeledAggregationConfigFns
  },
  severities: {
    groupName: { id: 'title_severityLevel' },
    ...labeledAggregationConfigFns
  },
  urls: {
    groupName: { id: 'title_urls' },
    ...labeledAggregationConfigFns
  },
  types: {
    groupName: { id: 'title_type' },
    ...labeledAggregationConfigFns
  },
  servers: {
    groupName: { id: 'title_servers' },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  },
  buildNumbers: {
    groupName: { id: 'title_notes_applicationVersions' }
  },
  environments: {
    groupName: { id: 'title_environments' },
    ...labeledAggregationConfigFns
  },
  modules: {
    groupName: { id: 'title_modules' },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  },
  applications: {
    groupName: { id: 'title_applications' },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  },
  routes: {
    groupName: { id: 'title_application_routes' },
    ...labeledAggregationConfigFns
  },
  technologies: {
    groupName: { id: 'title_technologies' },
    ...labeledAggregationConfigFns
  },
  applicationTags: {
    groupName: { id: 'title_tags_application' }
  },
  applicationImportances: {
    groupName: { id: 'title_appImportances' },
    ...labeledAggregationConfigFns
  },
  languages: {
    groupName: { id: 'title_programmingLanguages' },
    ...labeledAggregationConfigFns
  },
  sinks: {
    groupName: { id: 'title_sinks' },
    ...labeledAggregationConfigFns
  },
  bugtrackerStatuses: {
    groupName: { id: 'title_bugtrackerStatus' },
    ...labeledAggregationConfigFns
  },
  protectStatuses: {
    groupName: { id: 'title_protectStatus' },
    ...labeledAggregationConfigFns
  }
}

/**
 * Converts a date string to milliseconds.
 */
export function dateStringToMillis(dateString: string): number {
  const dateObj = new Date(dateString)
  return dateObj.getTime()
}

export function areVulnerabilitiesFromSameApplication(
  vulnerabilities: Array<{
    application: {
      id: string
      isParentApp?: boolean
      parentApplicationId?: string
    }
  }> = []
): boolean {
  const hasVulns = vulnerabilities.length > 0

  if (hasVulns) {
    const parentAppFound = vulnerabilities.find(
      (v) => v.application.isParentApp
    )

    if (parentAppFound) {
      const parentAppId = parentAppFound.application.id

      const restVulns = vulnerabilities.filter(
        (v) => v.application.id !== parentAppId
      )

      const areChildrenApps = restVulns.every(
        (v) => v.application.parentApplicationId === parentAppId
      )

      return areChildrenApps
    } else {
      const baseAppId = vulnerabilities[0].application.id

      return vulnerabilities.every((v) => baseAppId === v.application.id)
    }
  } else {
    return true
  }
}
